<template>
  <div>
    <v-container fluid class="px-6 py-6">
      <v-row>
        <v-col cols="12">
          <v-card class="card-shadow border-radius-xl mb-4">
            <v-card-title class="d-flex align-center justify-space-between">
              <div>
                <span class="text-typo font-weight-bold">Device Alerts</span> <br>
                <span class="ml-0 subtitle-1 grey--text ">{{ deviceId }}</span>
              </div>
            </v-card-title>
            <hr style="border: 1px solid #f8f8f8;"/>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4" class="text-center">
                  <div class="threshold-container">
                    <div class="threshold-label">Secure</div>
                    <div class="threshold-value green--text">
                      <v-icon color="green" small class="mr-1">mdi-check-circle</v-icon>
                      0° - {{ thresholds.secure }}° deviation
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-center">
                  <div class="threshold-container">
                    <div class="threshold-label">Warning</div>
                    <div class="threshold-value amber--text">
                      <v-icon color="amber" small class="mr-1">mdi-alert</v-icon>
                      {{ thresholds.secure + 1 }}° - {{ thresholds.critical - 1 }}° deviation
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-center">
                  <div class="threshold-container">
                    <div class="threshold-label">Critical</div>
                    <div class="threshold-value red--text">
                      <v-icon color="red" small class="mr-1">mdi-alert-octagon</v-icon>
                      {{ thresholds.critical }}°+ deviation
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card class="card-shadow border-radius-xl">
            <v-card-title class="d-flex align-center justify-space-between">
              <div>
                <span class="text-typo font-weight-bold">Alert History</span>
                <span class="ml-2 subtitle-1 grey--text">{{ deviceId }}</span>
              </div>
              <!-- <div>
                <v-select
                  v-model="torqueUnit"
                  :items="torqueUnitOptions"
                  dense
                  width="250"
                  outlined
                  hide-details
                  class="
                    torque-unit-select
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                  "
                ></v-select>
              </div> -->
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="alerts"
                :items-per-page="10"
                :loading="loading"
                class="elevation-0"
                flat
                @click:row="openAlertDetails"
                :item-class="rowClasses"
              >
                <template v-slot:[`item.timestamp`]="{ item }">
                  {{ formatTimestamp(item.timestamp) }}
                </template>
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:[`item.torqueAngleDeviation`]="{ item }">
                  {{ item.torqueAngleDeviation }}°
                </template>
                <template v-slot:[`item.looseningRate`]="{ item }">
                  {{ displayTorque(item.looseningRate) }} {{ torqueUnit }}/°
                </template>
                <template v-slot:[`item.torqueDeviation`]="{ item }">
                  {{ displayTorque(item.torqueDeviation) }} {{ torqueUnit }}
                </template>
                <template v-slot:[`item.batteryLevel`]="{ item }">
                  {{ item.batteryLevel }}%
                </template>
                <template v-slot:[`item.temperature`]="{ item }">
                  {{ item.temperature }}°F
                </template>
                <template v-slot:[`item.read`]="{ item }">
                  <v-icon
                    :color="item.read ? 'green' : 'red'"
                    small
                  >
                    {{ item.read ? 'mdi-check-circle' : 'mdi-bell' }}
                  </v-icon>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Alert Details Modal -->
    <alert-details-modal
      :show-modal.sync="showModal"
      :alert="selectedAlert"
      :torque-unit.sync="torqueUnit"
      :device-id="deviceId"
      @close="closeModal"
    />
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { 
  fetchSingleObjectByKey, 
  fetchObjects, 
  updateSingleObjectToFirebase,
  createWorkOrder,
  createInspection
} from "@/services/firebase-helpers";
import AlertDetailsModal from "@/components/AlertDetailsModal.vue";

export default {
  name: "AlertHistory",
  components: {
    AlertDetailsModal,
  },
  data() {
    return {
      deviceId: this.$route.params.deviceId,
      loading: true,
      alerts: [],
      showModal: false,
      selectedAlert: null,
      torqueUnit: "ft-lbs",
      torqueUnitOptions: [
        { text: "Newton meters (Nm)", value: "Nm" },
        { text: "Foot pounds (ft-lbs)", value: "ft-lbs" },
      ],
      thresholds: {
        secure: 8,   // Will be fetched from Firebase
        critical: 30 // Will be fetched from Firebase
      },
      headers: [
        { text: "Timestamp", value: "timestamp", width: "20%" },
        { text: "Status", value: "status", width: "15%" },
        {
          text: "Torque Angle Deviation",
          value: "torqueAngleDeviation",
          width: "15%",
        },
        { text: "Loosening Rate", value: "looseningRate", width: "15%" },
        { text: "Torque Deviation", value: "torqueDeviation", width: "15%" },
        { text: "Battery Level (%)", value: "batteryLevel", width: "10%" },
        { text: "Temperature (°F)", value: "temperature", width: "10%" },
        { text: "Read Status", value: "read", width: "10%" },
      ],
      // New properties for inspection scheduling
      inspectionDate: null,
      inspectionTime: null,
      inspectionComplete: false,
      
      // New properties for work order creation
      workOrderProcessing: false,
      workOrderComplete: false,
      workOrderNumber: null,
    };
  },
  computed: {
    // Conversion factor for Nm to lb-ft
    conversionFactor() {
      return 0.737562;
    },
    
    // Available time slots
    availableTimeSlots() {
      // Create time slots from 9am to 5pm with some disabled to simulate busyness
      const slots = [];
      const disabledSlots = this.getDisabledTimeSlots();
      
      for (let hour = 9; hour <= 16; hour++) {
        const amPm = hour < 12 ? 'AM' : 'PM';
        const hour12 = hour > 12 ? hour - 12 : hour;
        const timeValue = `${hour}:00`;
        const timeLabel = `${hour12}:00 ${amPm}`;
        
        slots.push({
          value: timeValue,
          label: timeLabel,
          disabled: disabledSlots.includes(timeValue)
        });
      }
      
      return slots;
    },
    
    // Formatted inspection date and time for confirmation message
    formatInspectionDate() {
      if (!this.inspectionDate || !this.inspectionTime) return '';
      
      const date = DateTime.fromISO(this.inspectionDate);
      const formattedDate = date.toFormat('MMMM d, yyyy');
      
      // Convert time from 24hr "14:00" to "2:00 PM"
      const [hour] = this.inspectionTime.split(':');
      const hour12 = hour > 12 ? hour - 12 : hour;
      const amPm = hour < 12 ? 'AM' : 'PM';
      
      return `${formattedDate} at ${hour12}:00 ${amPm}`;
    }
  },
  methods: {
    formatTimestamp(timestamp) {
      return DateTime.fromISO(timestamp).toFormat("MMM d, yyyy HH:mm");
    },

    getStatusColor(status) {
      switch (status) {
        case "Critical":
          return "red";
        case "Warning":
          return "amber";
        case "Secure":
          return "green";
        default:
          return "grey";
      }
    },

    getStatusClass(status) {
      switch (status) {
        case "Critical":
          return "red--text";
        case "Warning":
          return "amber--text";
        case "Secure":
          return "green--text";
        default:
          return "";
      }
    },

    displayTorque(value) {
      if (value === undefined || value === null) return "0.0";
      
      const numValue = Number(value);
      if (isNaN(numValue)) return "0.0";
      
      if (this.torqueUnit === "ft-lbs") {
        return (numValue * this.conversionFactor).toFixed(1);
      }
      return numValue.toFixed(1);
    },

    openAlertDetails(alert) {
      this.selectedAlert = alert;
      this.showModal = true;
      
      // Reset all views
      this.inspectionDate = null;
      this.inspectionTime = null;
      this.inspectionComplete = false;
      
      // If the alert is unread, mark it as read and update Firebase
      if (alert && !alert.read) {
        try {
          // Update locally
          alert.read = true;
          alert.readTimestamp = new Date().toISOString();
          
          // Update in Firebase - but only for real alerts (not the default first alert)
          if (alert.id !== 1) {
            updateSingleObjectToFirebase({
              ...alert,
              read: true,
              readTimestamp: alert.readTimestamp
            }, "alerts", alert.key || alert.id.toString());
            
            console.log(`Marked alert ${alert.id} as read at ${alert.readTimestamp}`);
          } else {
            console.log(`Marked default alert as read locally at ${alert.readTimestamp}`);
          }
        } catch (error) {
          console.error("Error updating alert read status:", error);
        }
      }
    },

    async loadAlerts() {
      // Show loading state
      this.loading = true;
      
      // First, fetch device thresholds from Firebase
      try {
        const deviceData = await fetchSingleObjectByKey("devices", this.deviceId);
        if (deviceData && deviceData.thresholds) {
          // Convert from green/yellow/red to secure/warning/critical
          this.thresholds = {
            secure: deviceData.thresholds.yellow - 1,
            critical: deviceData.thresholds.red
          };
          console.log("Loaded device thresholds:", this.thresholds);
        } else {
          // Fallback to default thresholds if not found
          console.log("Using default thresholds - device data not found");
          this.thresholds = {
            secure: 14,
            critical: 45
          };
        }
      } catch (error) {
        console.error("Error fetching device thresholds:", error);
        // Use fallback thresholds
        this.thresholds = {
          secure: 14,
          critical: 45
        };
      }
      
      // Create the default first alert that matches DeviceDetail.vue
      const defaultFirstAlert = {
        id: 1,
        deviceId: this.deviceId,
        timestamp: new Date().toISOString(),
        status: "Warning",
        initialTorque: 120,
        currentTorque: 110,
        initialAngle: 0,
        torqueAngleDeviation: 42,
        looseningRate: -5.2,
        torqueDeviation: -10,
        batteryLevel: 82,
        temperature: 75,
        riskMessage: "Fastener loosening at a rate of 5.2 Nm per degree. Inspection recommended.",
        read: false
      };
      
      // Fetch real alerts from Firebase for this device
      try {
        // Get all alerts from Firebase
        const allAlerts = await fetchObjects("alerts");
        
        if (!allAlerts || allAlerts.length === 0) {
          console.log("No alerts found in Firebase");
          // Even if no alerts found, still show the default first alert
          this.alerts = [defaultFirstAlert];
          this.loading = false;
          return;
        }
        
        // Filter alerts for the current device
        const deviceAlerts = allAlerts.filter(alert => alert.deviceId === this.deviceId);
        
        if (deviceAlerts.length === 0) {
          console.log(`No alerts found for device ${this.deviceId}`);
          // Even if no alerts found, still show the default first alert
          this.alerts = [defaultFirstAlert];
          this.loading = false;
          return;
        }
        
        console.log(`Found ${deviceAlerts.length} alerts for device ${this.deviceId}`);
        
        // Sort alerts by timestamp (newest first)
        deviceAlerts.sort((a, b) => {
          const dateA = new Date(a.timestamp);
          const dateB = new Date(b.timestamp);
          return dateB - dateA;
        });
        
        // Combine default first alert with real alerts
        this.alerts = [defaultFirstAlert, ...deviceAlerts];
      } catch (error) {
        console.error("Error fetching alerts:", error);
        // Even if error, still show the default first alert
        this.alerts = [defaultFirstAlert];
      } finally {
        this.loading = false;
      }
    },

    // New and modified methods for schedule inspection and create work order
    scheduleInspection() {
      this.showScheduler = true;
    },
    
    cancelScheduling() {
      this.showScheduler = false;
      this.inspectionDate = null;
      this.inspectionTime = null;
    },
    
    confirmInspection() {
      // Create inspection data
      const inspectionData = {
        title: `Scheduled Inspection - Device ${this.deviceId}`,
        description: `Inspection scheduled for alert on device ${this.deviceId}. ${this.selectedAlert.riskMessage}`,
        deviceId: this.deviceId,
        locationId: 'location_001', // You can get this from device data
        alertId: this.selectedAlert.id,
        scheduledDate: this.inspectionDate,
        scheduledTime: this.inspectionTime,
        inspector: 'Inspection Team',
        estimatedDuration: 1.5,
        notes: `Inspection requested for ${this.selectedAlert.status} alert`,
        alertDetails: {
          torqueAngleDeviation: this.selectedAlert.torqueAngleDeviation,
          looseningRate: this.selectedAlert.looseningRate,
          torqueDeviation: this.selectedAlert.torqueDeviation,
          batteryLevel: this.selectedAlert.batteryLevel,
          temperature: this.selectedAlert.temperature
        }
      };
      
      // Create inspection in Firebase
      createInspection(inspectionData)
        .then((result) => {
          this.showScheduler = false;
          this.inspectionComplete = true;
          console.log('Inspection scheduled successfully:', result);
        })
        .catch((error) => {
          console.error('Failed to schedule inspection:', error);
          // Still show success for demo purposes
          this.showScheduler = false;
          this.inspectionComplete = true;
        });
    },

    createWorkOrder() {
      this.workOrderProcessing = true;
      
      // Create work order data
      const workOrderData = {
        title: `Critical Alert Response - ${this.selectedAlert.status}`,
        description: `Work order created for alert on device ${this.deviceId}. ${this.selectedAlert.riskMessage}`,
        deviceId: this.deviceId,
        locationId: 'location_001', // You can get this from device data
        alertId: this.selectedAlert.id,
        priority: this.selectedAlert.status === 'Critical' ? 'High' : 'Medium',
        assignedTo: 'Maintenance Team',
        estimatedHours: this.selectedAlert.status === 'Critical' ? 4 : 2,
        alertDetails: {
          torqueAngleDeviation: this.selectedAlert.torqueAngleDeviation,
          looseningRate: this.selectedAlert.looseningRate,
          torqueDeviation: this.selectedAlert.torqueDeviation,
          batteryLevel: this.selectedAlert.batteryLevel,
          temperature: this.selectedAlert.temperature
        }
      };
      
      // Create work order in Firebase
      createWorkOrder(workOrderData)
        .then((result) => {
          this.workOrderProcessing = false;
          this.workOrderComplete = true;
          this.workOrderNumber = result.workOrderNumber;
          console.log('Work order created successfully:', result);
        })
        .catch((error) => {
          console.error('Failed to create work order:', error);
          this.workOrderProcessing = false;
          // Still show success for demo purposes
          this.workOrderComplete = true;
          this.workOrderNumber = "WO-" + Math.floor(100000 + Math.random() * 900000);
        });
    },
    
    closeModal() {
      this.showModal = false;
      this.resetModalState();
    },
    
    resetModalState() {
      this.showScheduler = false;
      this.inspectionDate = null;
      this.inspectionTime = null;
      this.inspectionComplete = false;
      this.workOrderProcessing = false;
      this.workOrderComplete = false;
      this.workOrderNumber = null;
    },
    
    // Helper methods for the scheduler
    allowedDates(date) {
      // Don't allow weekends or past dates
      const day = new Date(date).getDay();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return day !== 0 && day !== 6 && new Date(date) >= today;
    },
    
    getDisabledTimeSlots() {
      // Randomly disable some time slots to simulate busy schedule
      // Different disabled slots for different dates
      if (!this.inspectionDate) return [];
      
      const dateHash = new Date(this.inspectionDate).getDate();
      const disabledCount = 3 + (dateHash % 4); // 3-6 disabled slots
      
      const allSlots = Array.from({length: 8}, (_, i) => `${i+9}:00`);
      const shuffled = [...allSlots].sort(() => 0.5 - Math.random());
      
      return shuffled.slice(0, disabledCount);
    },

    rowClasses(item) {
      // Apply unread-alert class to unread alerts
      return !item.read ? 'unread-alert' : '';
    },
  },
  mounted() {
    this.loadAlerts();
  },
  
  watch: {
    // Watch the alerts array to open the specified alert once data is loaded
    alerts(newAlerts) {
      if (newAlerts.length > 0) {
        // Check if we need to open a specific alert from the query params
        const alertToOpen = this.$route.query.openAlert;
        if (alertToOpen) {
          // Find the alert with the matching ID
          const alertObj = newAlerts.find(alert => 
            alert.id.toString() === alertToOpen.toString()
          );
          
          if (alertObj) {
            console.log(`Opening alert ${alertToOpen} automatically`);
            // Set timeout to ensure UI is ready
            setTimeout(() => {
              this.openAlertDetails(alertObj);
            }, 300);
          }
        }
      }
    },
    // Add new watch for inspection date to refresh time slots
    inspectionDate() {
      // Reset the time selection when date changes
      this.inspectionTime = null;
    }
  }
};
</script>

<style scoped>
.metric {
  margin-bottom: 12px;
}

.metric .label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.metric .value {
  font-size: 16px;
  font-weight: 500;
}

.metric.risk .value {
  font-size: 14px;
  margin-top: 4px;
}

.threshold-container {
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 5px;
}

.threshold-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 5px;
}

.threshold-value {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.unread-alert {
  background-color: rgba(25, 118, 210, 0.08) !important;
  font-weight: 500;
}

.unread-alert td:first-child {
  border-left: 4px solid #1976D2 !important;
}

.v-data-table >>> tbody tr.unread-alert:hover {
  background-color: rgba(25, 118, 210, 0.12) !important;
}

/* New styles for scheduler and work order */
.time-slot-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 15px;
}

.work-order-processing,
.work-order-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

/* Updated styles for scheduler */
.full-width-calendar {
  width: 100% !important;
  background-color: #1e1e1e !important;
}

.time-slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-top: 15px;
}

.time-slot-btn {
  height: 44px !important;
}

/* Dark theme overrides for calendar */
:deep(.v-date-picker-table) {
  color: white !important;
}

:deep(.v-date-picker-header) {
  color: white !important;
}

:deep(.v-date-picker-table__events) {
  color: white !important;
}
</style>
